import { Component, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { TranslateService } from '@ngx-translate/core';
import { NetworkService } from '../../providers/network.service';
import { StorageKey, StorageService } from '../../providers/storage.service';
import { translationObject } from 'src/constants';
import { Preferences } from '@capacitor/preferences';
import { Subscription } from 'rxjs';
import { LanguageSelected } from '../../view-models/login_data';
import { AuthService } from '../../providers/auth.service';
import { UtilityService } from '../../providers/utility.service';

@Component({
  selector: 'app-root',
  templateUrl: 'root.component.html',
  styleUrls: ['root.component.scss']
})

export class RootComponent implements OnInit {
  /** Subscribes to the language translation data saved on the device. */
  languageSubscription: Subscription;
  /** A flag used to determine initialisation occurring (true) or not. So that default translations are only loaded once. */
  appInitialisation: boolean;

  /**
   * Standard Angular Constructor
   *
   * @ignore
   */
  constructor(
    /* eslint-disable */
    private networkService: NetworkService,
    private platform: Platform,
    private splashScreen: SplashScreen,
    private statusBar: StatusBar,
    private storageService: StorageService,
    private translate: TranslateService,
    private authService: AuthService,
    private utilityService: UtilityService
    /* eslint-enable */
  ) {
    this.platform.ready().then(async () => {
      await this.onPlatformReady();
    });
  }

  /**
   * Angular Lifecycle method.
   */
  ngOnInit() {
    this.appInitialisation = true;
    this.setLocale();
    this.utilityService.setStatusBarColour('#000000');
    this.setGlobalTranslation();
    Preferences.migrate();
  }

  /**
   * Sets the locale.
   */
  setLocale() {
    this.translate.setDefaultLang('en');
    if (window.Intl && typeof window.Intl === 'object') {
      // https://developer.mozilla.org/en-US/docs/Web/API/NavigatorLanguage/language
      // https://appmakers.dev/bcp-47-language-codes-list/
      // This will try to load the locale file @ ./assets/i18n/x.json, if it doesn't exist
      // the default locale will be used.
      this.translate.use(navigator.language.indexOf('-') ? navigator.language.split('-')[0] : navigator.language);
    }
  }

  /**
   * Sets the default translations in the en.json file to the global translation object
   * on opening the app. If the default translations could not be loaded then a console error
   * log is made.
   * Can get triggered a second time when the first language selection is made. If there is a web translation for the selection made then that is used instead.
   */
  setGlobalTranslation() {
    let code: string;
    this.languageSubscription = this.storageService.findItem(StorageKey.LANGUAGE).subject.subscribe(async (language: LanguageSelected) => {
      if (this.appInitialisation && language !== null) { // Condition is met on start up when there is a language selection made and on the first selection made after start up.
        this.appInitialisation = false;
        if (language) {
          code = language.code;
        } else {
          code = 'en';
        }
        let defaultTranslations;
        if (code === 'en') {
          defaultTranslations = this.authService.englishTranslations ? this.authService.englishTranslations : require('src/assets/i18n/en.json');
        } else {
          defaultTranslations = this.authService.frenchCanadianTranslations ? this.authService.frenchCanadianTranslations : require('src/assets/i18n/fr-ca.json');
        }
        if (defaultTranslations !== 'undefined') {
          translationObject.currentTranslation = defaultTranslations;
        } else {
          console.log('#### Could not load default translations');
        }
      } else if (this.appInitialisation) {  // Condition is met on startup if there is no language selection. English is the default.
        translationObject.currentTranslation = require('src/assets/i18n/en.json');
      }
    });
  }

  /**
   * Sets platform readiness
   */
  async onPlatformReady() {
    this.statusBar.styleDefault();
    this.splashScreen.hide();
    this.networkService.onAppInit();
    await this.storageService.onAppInit();
  }
}
